import { show } from "@intercom/messenger-js-sdk";
import { Box, Typography } from "@mui/joy";
import {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  callSeatLabel,
  callSeatPlan,
  callSeats,
} from "../entities/callChairs";
import {
  callVenues,
  venueRowLabel,
  venueRowMapURL,
} from "../entities/callVenues";
import { CallTheme } from "../entities/callDesign";
import { MissionMap } from "../entities/missionMap";
import { AskForRelease } from "../organisms/askForRelease";
import { Message, Piece } from "../entities/missionMap/missionMap";
import { Response } from "../organisms/response";
import { addToCalendar } from "../organisms/schedule";

/**
 * The modern call page (power-up `modernCalls`): the same layout as the modern
 * call email, block for block — masthead handled by the caller, then title,
 * greeting, the critical-note box, the response, and hairline sections for
 * schedule, repertoire, pay, dress code, links and contacts. No cards, no
 * centered chrome: what the musician opened from the email is what they see.
 *
 * Renders ONLY when the mapping carries ModernCall — the caller gates on it —
 * so legacy organizations keep the pre-existing page untouched.
 */

export const INK = "#0f1216";
const BODY = "#23272e";
export const SOFT = "#4b5563";
export const MUTED = "#6b7280";
export const HAIRLINE = "1px solid #e3e7ec";
const ROWLINE = "1px solid #f0f2f5";

/** The Rhapsody wordmark (992×275 viewBox — keep boxes at ~3.6:1), used as a
 * CSS mask so it renders in whatever color the footer text wears. Served
 * from public/ because mask-image is a CORS-mode fetch — the GCS copy has
 * no CORS headers and silently paints nothing. */
const RHAPSODY_LOGO = "/rhapsody-logo.svg";
const RHAPSODY_SITE = "https://www.rhapsody.la";

/** In-section memo lines (chair, session, piece) wear the same dress as the
 * top Memo box — background, rule and ink — just without the label. */
export const MEMO_QUOTE = {
  fontSize: 12.5,
  color: "#3d444d",
  background: "#fdf6ec",
  borderLeft: "3px solid #b4720c",
  px: 1.5,
  py: 1,
  mt: 0.75,
};

/** Quill records alignment as classes (ql-align-*); fresh mappings arrive
 * with the styles inlined server-side, and these cover markup stored before
 * that rewrite. */
export const QUILL_ALIGN_SX = {
  "& .ql-align-center": { textAlign: "center" },
  "& .ql-align-right": { textAlign: "right" },
  "& .ql-align-justify": { textAlign: "justify" },
} as const;

/** Quill body blocks (dress code, legacy notes) keep the editor's spacing —
 * the email's .rich rule, mirrored: inside the editor a paragraph has no
 * margin at all and a blank line is one empty <p> worth of air. Without this
 * the browser's default 1em paragraph margins turn every blank line the
 * organization typed into ~50px of white space (14 + an empty line + 14). */
export const QUILL_RICH_SX = {
  "& p": { margin: 0 },
  "& ul, & ol": { margin: 0, paddingLeft: "22px" },
  "& blockquote": {
    margin: 0,
    paddingLeft: "12px",
    borderLeft: "3px solid #e3e7ec",
  },
} as const;

/** Human titles of the reorderable sections, for the section headers and the
 * on-page table of contents alike. */
const SECTION_LABELS: { [id: string]: string } = {
  schedule: "Schedule",
  featuredArtists: "Featured artists",
  repertoire: "Repertoire",
  pay: "Pay",
  dressCode: "Dress code",
  links: "Files & links",
  rosters: "Rosters",
  notes: "Notes",
  contacts: "Contacts",
};

/** Anchor id of a section, shared by the section and the ToC links. */
export function callSectionAnchor(id: string): string {
  return `call-section-${id}`;
}

/** A display phone number stripped to a dialable tel: target — digits plus
 * one leading "+", so "+1 (212) 203-7020" dials cleanly. Empty (no link)
 * when the value carries no digits at all. Mirrors mercury's telLink. */
export function telHref(phone: string): string {
  const stripped = phone.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return /\d/.test(stripped) ? stripped : "";
}

/**
 * The messages a musician can actually read on the page.
 *
 * Mercury models a contract as a message, but to a musician it is an ask with
 * its own page and its own signature — listing it under "Messages" would invite
 * someone to read a contract as news. Shared with organisms/callMessages so the
 * block and its table-of-contents entry can never disagree about whether there
 * is anything there.
 */
export function readableCallMessages(messages?: Message[]): Message[] {
  return (messages ?? []).filter((message) => message.Name !== "Contract");
}

/** The org's order (Branding page) merged with the default: picked ids first,
 * unlisted appended — mirrors the backend. */
export function orderedCallSections(mission: MissionMap): string[] {
  const picked = (mission.CallSectionOrder ?? []).filter((id) =>
    DEFAULT_SECTION_ORDER.includes(id),
  );
  return [
    ...picked,
    ...DEFAULT_SECTION_ORDER.filter((id) => !picked.includes(id)),
  ];
}

/** Whether this call still states its dates. A musician who has left the
 * project — declined, released, cancelled: any terminus stage — keeps the call
 * for reference but not the schedule, which is what the classic page has
 * always done (organisms/schedule.tsx returns nothing on a terminus). The
 * modern call must not quietly hand out what the old one withheld.
 *
 * @param mission the call's mapping
 * @returns {boolean} true when the schedule block belongs on the page
 */
export function showsSchedule(mission: MissionMap): boolean {
  return (
    Boolean(mission.Engagements?.length) && !mission.CurrentStage?.Terminus
  );
}

/**
 * The repertoire rows worth showing. A row with a title is a work. A row
 * without one is a seat: a v2 project with no library works holds its chairs
 * on a placeholder piece (pieceID 0), and that row's seat line is the only
 * place the call names the musician's chair — the email prints it, so the page
 * must too (hiding it left musicians asking which chair the call was for).
 *
 * A title-less row is said once per distinct seat, so when the backend's
 * librarian lookup fails (rhapsody-service#319) its N works collapse into one
 * seat line instead of N identical "Violin II · Principal · Violin" rows. A
 * title-less row with nothing else to say is dropped.
 *
 * @param mission the call's mapping
 * @returns {Piece[]} the works, then each title-less seat once, in order
 */
function repertoirePieces(mission: MissionMap): Piece[] {
  const seats = new Set<string>();

  return (mission.Pieces ?? []).filter((piece) => {
    if (piece.Title) return true;
    if (!piece.SectionName && !piece.Description && !piece.Memo) return false;

    const seat = JSON.stringify([
      piece.SectionName,
      piece.SectionRole,
      piece.Instruments,
      piece.Description,
      piece.Memo,
    ]);
    if (seats.has(seat)) return false;
    seats.add(seat);
    return true;
  });
}

/** The blocks that will actually render for this mission, in order — the
 * entries of the on-page table of contents. Uses the sections' own render
 * conditions, so the ToC never points at a block that isn't there. */
export function visibleCallSections(
  mission: MissionMap,
): { anchor: string; label: string }[] {
  const hasData: { [id: string]: boolean } = {
    schedule: showsSchedule(mission),
    featuredArtists: Boolean(mission.FeaturedArtists?.length),
    repertoire: repertoirePieces(mission).length > 0,
    // Any of the three, not just Wage. Wage is the project's free-text agreement
    // line, empty by definition on a project using automatic wages — so keying
    // the block on it alone hid pay, and its "On this page" entry with it, from
    // exactly the projects whose pay is computed.
    pay: Boolean(
      mission.Wage || mission.WageDescription || mission.WageBreakdown,
    ),
    dressCode: Boolean(mission.DressCode?.length),
    links: Boolean(mission.Links?.length),
    rosters: Boolean(mission.rosters().length),
    // The legacy blob fallback; org note sections are handled below.
    notes: Boolean(mission.ProjectNotes?.length),
    contacts: Boolean(mission.Contacts?.length),
  };

  // Messages are pinned above the reorderable blocks — they are not part of
  // the org's section order (nor of the backend's callSectionIDs), so the entry
  // is prepended here rather than living in DEFAULT_SECTION_ORDER.
  const pinned = readableCallMessages(mission.Messages).length
    ? [{ anchor: callSectionAnchor("messages"), label: "Messages" }]
    : [];

  return [
    ...pinned,
    ...orderedCallSections(mission).flatMap((id) => {
      // Each org note renders as its own block titled by the note's name —
      // the ToC mirrors that instead of pointing at a single "Notes".
      if (id === "notes" && mission.CallNoteSections?.length)
        return mission.CallNoteSections.map((note, index) => ({
          anchor: callSectionAnchor(`notes-${index}`),
          label: note.Title || "Notes",
        }));
      if (!hasData[id]) return [];
      return [{ anchor: callSectionAnchor(id), label: SECTION_LABELS[id] }];
    }),
  ];
}

/** Default order of the reorderable sections; mirrors the backend's
 * callSectionIDs — sorted by when a musician needs the answer, not by
 * importance: contacts when you are late, featured artists for who is on the
 * podium, repertoire for your part and scale, then schedule, then pay, then
 * the utilities in date order. */
const DEFAULT_SECTION_ORDER = [
  "contacts",
  "featuredArtists",
  "repertoire",
  "schedule",
  "pay",
  "links",
  "rosters",
  "dressCode",
  "notes",
];

/**
 * A section's name, in the organization's own colour with its rule directly
 * underneath. A grey heading under a grey rule read as one undifferentiated
 * column — musicians could not see where a block started or ended.
 *
 * @param root0 props
 * @param root0.children the section's name
 * @param root0.theme resolved call theme (accent already corrected for text)
 * @returns {ReactElement} the heading
 */
function SectionLabel({
  children,
  theme,
}: {
  children: ReactNode;
  theme: CallTheme;
}) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: theme.accentText,
        pt: 2.25,
        pb: 0.75,
        borderBottom: `1px solid ${theme.accent}`,
      }}
    >
      {children}
    </Typography>
  );
}

function Section({
  label,
  anchor,
  theme,
  children,
}: {
  label: string;
  anchor?: string;
  theme: CallTheme;
  children: ReactNode;
}) {
  return (
    <Box
      id={anchor}
      sx={{
        width: "100%",
        textAlign: "left",
        // Anchored jumps land with breathing room instead of flush to the
        // top — and below lg, clear of the sticky "On this page" bar.
        scrollMarginTop: { xs: "56px", lg: "16px" },
      }}
    >
      <SectionLabel theme={theme}>{label}</SectionLabel>
      {children}
    </Box>
  );
}

/**
 * @param root0 props
 * @param root0.mission the public mission map
 * @param root0.theme resolved call theme (modern, accent already corrected)
 * @param root0.token the public token, forwarded to the functional organisms
 * @param root0.hideResponse embedding contexts that answer elsewhere
 * @param root0.hideGreeting contexts that already showed the greeting
 * @returns {ReactElement} the modern call content column
 */
export function ModernInviteContent({
  mission,
  theme,
  token,
  hideResponse,
  hideFooter,
  hideGreeting,
  messages,
}: {
  mission: MissionMap;
  theme: CallTheme;
  token: string;
  hideResponse?: boolean;
  hideFooter?: boolean;
  hideGreeting?: boolean;
  /** The inline Messages block, passed in rather than rendered here: a season
   * composes this content once per project card, and the season's messages
   * belong to the season page, not repeated under every project. */
  messages?: ReactNode;
}): ReactElement {
  const link = {
    color: theme.accentText,
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: 14.5,
  };

  // What the sessions have in common gets said once; only a session that
  // deviates from it — or carries a memo — is worth a box. Computed across every
  // group's sessions, since Engagements groups by type (rehearsals, then
  // performances) and the seating question spans the whole call.
  const seatPlan = callSeatPlan(
    (mission.Engagements ?? []).flatMap((group) => group.Content ?? []),
  );

  // Each addressed venue is introduced once, above the sessions — the rows
  // then say only its short label instead of the full address every time.
  const venues = callVenues(mission.Engagements);

  // A starred start label ("3:30PM*") means the project's times are
  // approximate — the footnote below the sessions says so. Keyed off the
  // labels themselves so the footnote can never appear without a star.
  const hasEstimatedTimes = (mission.Engagements ?? []).some((group) =>
    (group.Content ?? []).some((engagement) =>
      (engagement.Plays ?? []).some((play) => play.StartLabel?.endsWith("*")),
    ),
  );

  // Same rule as the ToC entry, so the header never appears over zero rows.
  const pieces = repertoirePieces(mission);

  const sections: { [id: string]: ReactNode } = {
    schedule: (
      <>
          {/* schedule: day column left, what right */}
          {showsSchedule(mission) ? (
            <Section theme={theme} label="Schedule" anchor={callSectionAnchor("schedule")}>
              {/* the seat the whole call shares, stated once and plainly */}
              {seatPlan.common ? (
                <Typography sx={{ fontSize: 12.5, color: SOFT, pb: 0.5 }}>
                  {seatPlan.common}
                </Typography>
              ) : null}
              {/* each addressed venue introduced once — name linked to maps,
                  short label in parentheses, address under it — so the rows
                  below never repeat the address */}
              {venues.length ? (
                <Box sx={{ width: "100%", pb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#9aa1ab",
                      pb: 0.5,
                    }}
                  >
                    Locations
                  </Typography>
                  {venues.map((venue) => (
                    <Box key={`${venue.name}\n${venue.address}`} sx={{ pb: 0.75 }}>
                      <Typography sx={{ fontSize: 13, color: SOFT }}>
                        <Box
                          component="a"
                          href={venue.mapURL}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ color: link.color, textDecoration: "underline" }}
                        >
                          {venue.name}
                        </Box>
                        {venue.shortName ? ` (${venue.shortName})` : ""}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        {venue.address}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}
              {mission.Engagements.filter((group) => group.Content?.length).map(
                (group) => (
                  <Box key={group.Type} sx={{ width: "100%" }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#9aa1ab",
                        pb: 0.5,
                      }}
                    >
                      {group.Type}
                    </Typography>
                    {group.Content.map((engagement) => {
                      // "Not playing" must not lean on red vs green alone:
                      // the row dims and the date is struck through, so the
                      // state reads for colorblind musicians too. Only when
                      // the full schedule is shown — otherwise every row the
                      // musician sees is one they play.
                      const benched =
                        Boolean(mission.ShowFullSchedule) && !engagement.Playing;
                      return (
                      <Box
                        key={engagement.ID}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          width: "100%",
                          py: 1,
                          borderBottom: ROWLINE,
                          flexWrap: "wrap",
                          // a light striped band so "not yours" reads at a
                          // glance, before any text or color does
                          ...(benched
                            ? {
                                backgroundColor: "#f8f9fb",
                                backgroundImage:
                                  "repeating-linear-gradient(135deg, #f8f9fb 0px, #f8f9fb 10px, #eef0f3 10px, #eef0f3 20px)",
                              }
                            : {}),
                        }}
                      >
                        <Box sx={{ flex: "0 0 128px" }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: benched ? "#9aa1ab" : INK,
                              textDecoration: benched ? "line-through" : "none",
                            }}
                          >
                            {engagement.WeekDay} {engagement.Month} {engagement.Day}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 13, color: benched ? "#9aa1ab" : MUTED }}
                          >
                            {engagement.HourRange}
                            {engagement.Timezone ? ` ${engagement.Timezone}` : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: benched ? MUTED : INK,
                            }}
                          >
                            {engagement.Title}
                            {mission.ShowFullSchedule ? (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: engagement.Playing
                                    ? "#16653f"
                                    : "#a8341f",
                                  ml: 1,
                                }}
                              >
                                {engagement.Playing ? "Playing" : "Not playing"}
                              </Box>
                            ) : null}
                          </Typography>
                          {/* the short label — Locations above carries the
                              full name and address once; the row label links
                              to the same maps search */}
                          {venueRowLabel(engagement) ? (
                            <Typography sx={{ fontSize: 13, color: SOFT }}>
                              {venueRowMapURL(engagement) ? (
                                <Box
                                  component="a"
                                  href={venueRowMapURL(engagement)}
                                  target="_blank"
                                  rel="noreferrer"
                                  sx={{
                                    color: link.color,
                                    textDecoration: "underline",
                                  }}
                                >
                                  {venueRowLabel(engagement)}
                                </Box>
                              ) : (
                                venueRowLabel(engagement)
                              )}
                            </Typography>
                          ) : null}
                          {engagement.Conductor ? (
                            <Typography sx={{ fontSize: 13, color: SOFT }}>
                              {engagement.Conductor}, conductor
                            </Typography>
                          ) : null}
                          {/* the musician's seat FOR THIS SESSION, in the same
                              amber box as its memo — a per-work-session override
                              is the thing most worth noticing in a schedule, and
                              as plain body copy it read as ordinary.

                              callSeats collapses seats identical in everything
                              shown: a v2 project seats per piece, so an override
                              on a three-piece session arrives as three chairs
                              and printed the role three times and the memo
                              three times. Mirrors mercury's callChairs so the
                              email and this page stay the same document. */}
                          {seatPlan.standsOut(callSeats(engagement.Chairs))
                            ? callSeats(engagement.Chairs).map((seat, index) => {
                                const label = callSeatLabel(seat);
                                return (
                                  <Box key={index} sx={MEMO_QUOTE}>
                                    {label ? (
                                      <Typography
                                        sx={{ fontSize: 12.5, color: "inherit" }}
                                      >
                                        {label}
                                      </Typography>
                                    ) : null}
                                    {seat.memo ? (
                                      <Typography
                                        sx={{
                                          fontSize: 12.5,
                                          color: "inherit",
                                          pt: label ? 0.5 : 0,
                                        }}
                                      >
                                        {seat.memo}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                );
                              })
                            : null}
                          {engagement.Plays?.filter((play) => play.Title)
                            .length ? (
                            <Box sx={{ pt: 0.25 }}>
                              {engagement.Plays.filter((play) => play.Title).map(
                                (play, index) => (
                                  <Typography
                                    key={`${engagement.ID}-${index}`}
                                    sx={{ fontSize: 12.5, color: MUTED }}
                                  >
                                    &#8226;{" "}
                                    {play.StartLabel
                                      ? `${play.StartLabel} · `
                                      : ""}
                                    <em>{play.Title}</em>
                                    {play.Composer ? `, ${play.Composer}` : ""}
                                    {/* Only when this session plays a subset of what the
                                        project plays — the repertoire section
                                        carries the full movement list, and
                                        repeating it on every service is what
                                        buried the schedule. */}
                                    {play.SessionMovementsLabel
                                      ? ` (${play.SessionMovementsLabel})`
                                      : ""}
                                  </Typography>
                                ),
                              )}
                            </Box>
                          ) : null}
                          {engagement.Memo ? (
                            <Typography sx={MEMO_QUOTE}>
                              {engagement.Memo}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                      );
                    })}
                  </Box>
                ),
              )}
              {hasEstimatedTimes ? (
                <Typography sx={{ fontSize: 12, color: MUTED, pt: 1 }}>
                  * Times are estimates.
                </Typography>
              ) : null}
              <Typography
                sx={{ ...link, pt: 1.25 }}
                onClick={() => addToCalendar([mission])}
              >
                Add the schedule to your calendar
              </Typography>
            </Section>
          ) : null}
      </>
    ),
    featuredArtists: (
      <>
          {/* featured artists: org-authored rich text */}
          {mission.FeaturedArtists?.length ? (
            <Section theme={theme} label="Featured artists" anchor={callSectionAnchor("featuredArtists")}>
              <Box
                sx={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "#3d444d",
                  ...QUILL_RICH_SX,
                  ...QUILL_ALIGN_SX,
                }}
                dangerouslySetInnerHTML={{ __html: mission.FeaturedArtists }}
              />
            </Section>
          ) : null}
      </>
    ),
    repertoire: (
      <>
          {/* repertoire */}
          {pieces.length ? (
            <Section theme={theme} label="Repertoire" anchor={callSectionAnchor("repertoire")}>
              {pieces.map((piece, index) => (
                <Box
                  key={piece.ProjectPieceID ?? index}
                  sx={{ py: 1, borderBottom: ROWLINE, width: "100%" }}
                >
                  {/* A seat row (no title) starts at its seat line. */}
                  {piece.Title ? (
                    <Typography sx={{ fontSize: 14.5, color: "#16181d" }}>
                      <em>{piece.Title}</em>
                      {piece.Composer ? (
                        <Box component="span" sx={{ color: SOFT }}>
                          {" "}
                          &#8212; {piece.Composer}
                        </Box>
                      ) : null}
                    </Typography>
                  ) : null}
                  {/* The movements the project plays of this work: spelled out
                      one per line while they are few enough to read, kept as a
                      range once they are not (MovementsCompact) — a Carmina
                      burana is only readable as "I–XXV". */}
                  {piece.MovementsCompact ? (
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                      {piece.MovementsLabel}
                    </Typography>
                  ) : (
                    piece.Movements?.map((movement, movementIndex) => (
                      <Typography
                        key={`${piece.ProjectPieceID ?? index}-mvt-${movementIndex}`}
                        sx={{ fontSize: 12.5, color: MUTED }}
                      >
                        {movement}
                      </Typography>
                    ))
                  )}
                  {/* The work's instrumentation, after the movements and
                      before the seat — what a wind player reads to know
                      whether they are in the piece at all. Description IS the
                      project-scoped shorthand (the add dialog's Shorthand
                      column, inherited from the library work at creation);
                      pre-line keeps its Daniels-style line breaks. */}
                  {piece.Description ? (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: MUTED,
                        pt: 0.5,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {piece.Description}
                    </Typography>
                  ) : null}
                  {piece.SectionName ? (
                    // Set apart from the movement list above it: this is the
                    // musician's own part, not another line of the work.
                    <Typography
                      sx={{ fontSize: 12.5, color: INK, pt: piece.Title ? 0.875 : 0 }}
                    >
                      {piece.SectionName}
                      {piece.SectionRole ? ` · ${piece.SectionRole}` : ""}
                      {piece.Instruments?.length
                        ? ` · ${piece.Instruments.join(", ")}`
                        : ""}
                    </Typography>
                  ) : null}
                  {piece.Memo ? (
                    <Typography sx={MEMO_QUOTE}>{piece.Memo}</Typography>
                  ) : null}
                </Box>
              ))}
            </Section>
          ) : null}
      </>
    ),
    pay: (
      <>
          {/* pay — three tokens, in the same order the call email uses:
              the agreement line, then the terms a person wrote, then the
              computed breakdown. */}
          {mission.Wage || mission.WageDescription || mission.WageBreakdown ? (
            <Section theme={theme} label="Pay" anchor={callSectionAnchor("pay")}>
              {mission.Wage ? (
                <Typography sx={{ fontSize: 15, color: "#16181d" }}>
                  {mission.Wage}
                  {mission.WageUnit ? (
                    <Box component="span" sx={{ color: MUTED, fontSize: 13 }}>
                      {" "}
                      {mission.WageUnit}
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {mission.WageDescription ? (
                <Typography sx={{ fontSize: 15, color: "#16181d" }}>
                  {mission.WageDescription}
                </Typography>
              ) : null}
              {mission.WageBreakdown ? (
                // HTML from the server — built there so this page and the email
                // cannot disagree about what somebody is paid.
                // Box, not Typography: Joy's Typography injects children for its
                // decorators, and React refuses children alongside
                // dangerouslySetInnerHTML (error #60) — which took the whole call
                // page down. Every other HTML block on this page uses Box for the
                // same reason.
                <Box
                  sx={{ fontSize: 13, color: SOFT, pt: 0.5 }}
                  dangerouslySetInnerHTML={{ __html: mission.WageBreakdown }}
                />
              ) : null}
            </Section>
          ) : null}
      </>
    ),
    dressCode: (
      <>
          {/* dress code: org-authored rich text */}
          {mission.DressCode?.length ? (
            <Section theme={theme} label="Dress code" anchor={callSectionAnchor("dressCode")}>
              <Box
                sx={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "#3d444d",
                  ...QUILL_RICH_SX,
                  ...QUILL_ALIGN_SX,
                }}
                dangerouslySetInnerHTML={{ __html: mission.DressCode }}
              />
            </Section>
          ) : null}
      </>
    ),
    links: (
      <>
          {/* files & links */}
          {mission.Links?.length ? (
            <Section theme={theme} label="Files & links" anchor={callSectionAnchor("links")}>
              {mission.Links.map((missionLink, index) => (
                <Box key={index} sx={{ py: 1, borderBottom: ROWLINE, width: "100%" }}>
                  <Typography
                    component="a"
                    href={missionLink.URL}
                    target="_blank"
                    sx={link}
                  >
                    {missionLink.Name}
                  </Typography>
                  {missionLink.Description ? (
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                      {missionLink.Description}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Section>
          ) : null}
      </>
    ),
    rosters: (
      <>
          {/* rosters keep their functional organism-free flat row */}
          {mission.rosters().length ? (
            <Section theme={theme} label="Rosters" anchor={callSectionAnchor("rosters")}>
              {mission.rosters().map((roster, index) => (
                <Box key={index} sx={{ py: 1, borderBottom: ROWLINE, width: "100%" }}>
                  <Typography
                    component="a"
                    href={roster.URL}
                    target="_blank"
                    sx={link}
                  >
                    {roster.Name || "Roster"}
                  </Typography>
                </Box>
              ))}
            </Section>
          ) : null}
      </>
    ),
    notes: (
      <>
          {/* org note sections: each included note is a first-class section —
              label = the note's name, body sanitized server-side and retypeset
              here. This is how an org adds its own blocks (comp tickets, housing…)
              that blend with the built-ins. Falls back to the raw ProjectNotes
              blob for mappings generated before the sections existed. */}
          {mission.CallNoteSections?.length ? (
            mission.CallNoteSections.map((note, index) => (
              <Section
                key={index}
                theme={theme}
                label={note.Title || "Notes"}
                anchor={callSectionAnchor(`notes-${index}`)}
              >
                <Box
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: "#3d444d",
                    "& p": { margin: "0 0 8px 0" },
                    "& p:last-child": { marginBottom: 0 },
                    "& a": { color: theme.accentText },
                    // The sanitizer lets real headings and code blocks
                    // through now; keep the editor's tight spacing.
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      margin: "0 0 8px 0",
                      lineHeight: 1.3,
                    },
                    "& pre": { whiteSpace: "pre-wrap", margin: "0 0 8px 0" },
                  }}
                  dangerouslySetInnerHTML={{ __html: note.HTML }}
                />
              </Section>
            ))
          ) : mission.ProjectNotes?.length ? (
            <Section theme={theme} label="Notes" anchor={callSectionAnchor("notes")}>
              <Box
                sx={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "#3d444d",
                  ...QUILL_RICH_SX,
                  ...QUILL_ALIGN_SX,
                }}
                dangerouslySetInnerHTML={{ __html: mission.ProjectNotes }}
              />
            </Section>
          ) : null}
      </>
    ),
    contacts: (
      <>
          {/* contacts */}
          {mission.Contacts?.length ? (
            <Section theme={theme} label="Contacts" anchor={callSectionAnchor("contacts")}>
              {mission.Contacts.map((contact, index) => (
                <Box key={index} sx={{ py: 1, borderBottom: ROWLINE, width: "100%" }}>
                  <Typography sx={{ fontSize: 14.5, color: "#16181d" }}>
                    {contact.Name}
                    {contact.Role ? (
                      <Box component="span" sx={{ color: MUTED }}>
                        {" "}
                        · {contact.Role}
                      </Box>
                    ) : null}
                  </Typography>
                  {contact.Email || contact.Phone ? (
                    // A contact is for reaching: the email composes, the
                    // phone dials (telHref strips display formatting).
                    <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                      {contact.Email ? (
                        <Box
                          component="a"
                          href={`mailto:${contact.Email}`}
                          sx={{
                            color: link.color,
                            textDecoration: "underline",
                          }}
                        >
                          {contact.Email}
                        </Box>
                      ) : null}
                      {contact.Email && contact.Phone ? " · " : ""}
                      {contact.Phone ? (
                        telHref(contact.Phone) ? (
                          <Box
                            component="a"
                            href={`tel:${telHref(contact.Phone)}`}
                            sx={{
                              color: link.color,
                              textDecoration: "underline",
                            }}
                          >
                            {contact.Phone}
                          </Box>
                        ) : (
                          contact.Phone
                        )
                      ) : null}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Section>
          ) : null}
      </>
    ),
  };

  // The org's section order (Branding page): their ids first, every unlisted
  // section appended in default order — a future section never vanishes. The
  // masthead, title, note and response stay above; the footer below.
  const sectionOrder = orderedCallSections(mission);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        gap: 1.5,
        color: BODY,
        // The org's typeface pick, matching the email. Typography and buttons
        // inherit; icon fonts (<i class="fa-…">) are deliberately untouched.
        fontFamily: theme.fontStack,
        "& .MuiTypography-root, & .MuiButton-root, & .MuiTextarea-root": {
          fontFamily: "inherit",
        },
      }}
    >
      {/* title: which job — with the artwork the org uploaded, if any: a
          season's cover or a project's, exactly as the email shows it. The
          greeting stays full width below, as in the email. */}
      <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {mission.CoverURL || mission.ProjectLogo ? (
          <Box
            component="img"
            src={mission.CoverURL || mission.ProjectLogo}
            alt=""
            sx={{
              width: 108,
              height: "auto",
              alignSelf: "flex-start",
              borderRadius: "6px",
              display: "block",
            }}
          />
        ) : null}
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 26,
            lineHeight: 1.2,
            fontWeight: 700,
            letterSpacing: "-0.3px",
            color: INK,
          }}
        >
          {mission.ProjectName}
        </Typography>
        {mission.ScheduleDescription ? (
          <Typography sx={{ fontSize: 15, color: SOFT, mt: 0.5 }}>
            {mission.ScheduleDescription}
          </Typography>
        ) : null}
        {mission.MessengerName ? (
          <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 1.25 }}>
            From {mission.MessengerName}
          </Typography>
        ) : null}
        </Box>
      </Box>
        {mission.Greetings && !hideGreeting ? (
          // Org-authored rich text (Quill markup), same as the email renders:
          // alignment and color arrive inline, spacing matches the editor.
          // The greeting is the mercury status ("You have declined this
          // job"), so it wears the current stage's own color and icon —
          // the identity the legacy page's stage chip already speaks.
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "flex-start" }}>
            {mission.CurrentStage?.Icon ? (
              <Box
                component="img"
                src={mission.CurrentStage.Icon}
                alt=""
                sx={{ height: 15, width: 15, mt: "4px", flexShrink: 0 }}
              />
            ) : null}
            <Box
              sx={{
                fontSize: 15,
                lineHeight: 1.55,
                color: mission.CurrentStage?.Color || BODY,
                "& p": { margin: "0 0 8px 0" },
                "& p:last-child": { marginBottom: 0 },
                ...QUILL_ALIGN_SX,
              }}
              dangerouslySetInnerHTML={{ __html: mission.Greetings }}
            />
          </Box>
        ) : null}
        {/* The way out, under the status that says they are in — exactly where
            the classic call kept it (organisms/greetings.tsx). It shows itself
            only once the call has been answered and only where the org allows
            release, so it costs nothing on a call still waiting for a reply,
            and the mapping travels with it: no second fetch per project when a
            season renders these. */}
        <Box sx={{ mt: 1.25 }}>
          <AskForRelease token={token} mission={mission} />
        </Box>
      </Box>

      {/* a rush call is a race: say so, and what it is for, before the ask —
          the instrument used to show only on the confirmation, after the seat
          was won. Same dress as the Memo below. */}
      {mission.IsCattleCall && !mission.TooLate ? (
        <Box
          sx={{
            width: "100%",
            background: "#fdf6ec",
            borderLeft: "3px solid #b4720c",
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8a5709",
            }}
          >
            Rush call · first come, first served
          </Typography>
          {mission.CattleCallSection ? (
            <Typography sx={{ fontSize: 17, color: INK }}>
              This call is for <b>{mission.CattleCallSection}</b>.
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: 14, color: "#3d444d" }}>
            The first musicians to accept get the seats.
          </Typography>
        </Box>
      ) : null}

      {/* the one thing that changes the answer */}
      {mission.Memo ? (
        <Box
          sx={{
            width: "100%",
            background: "#fdf6ec",
            borderLeft: "3px solid #b4720c",
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8a5709",
            }}
          >
            Memo
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#3d444d" }}>
            {mission.Memo}
          </Typography>
        </Box>
      ) : null}

      {/* the ask */}
      {mission.allInteractors()?.length && !hideResponse ? (
        <Response token={token} />
      ) : (
        []
      )}

      {/* what arrived after the call went out — above the reference sections
          because it is news, below the ask because it does not change it */}
      {messages}

      {/* reorderable sections, in the org's order */}
      {sectionOrder.map((id) => (
        <Fragment key={id}>{sections[id]}</Fragment>
      ))}

      {/* footer: one line */}
      {!hideFooter ? <CallFooter companyName={mission.CompanyName} /> : null}
    </Box>
  );
}

/**
 * The modern call's footer: the Rhapsody wordmark painted in the footer
 * text's own color linking home, why this arrived, and a way to reach us.
 * Shared by the call page and the season page so they cannot drift.
 *
 * @param root0 props
 * @param root0.companyName the org whose call this is
 * @returns {ReactElement} the footer
 */
export function CallFooter({
  companyName,
}: {
  companyName?: string;
}): ReactElement {
  return (
    <Box sx={{ width: "100%", borderTop: HAIRLINE, pt: 1.5, pb: 2 }}>
      {/* the mark links home; the mask paints the wordmark in the
          footer text's own color */}
      <Box
        component="a"
        href={RHAPSODY_SITE}
        target="_blank"
        aria-label="Rhapsody"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          width: "fit-content",
          textDecoration: "none",
          color: SOFT,
          "&:hover": { color: INK, textDecoration: "none" },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>
          Powered by
        </Typography>
        <Box
          aria-hidden
          sx={{
            height: 15,
            width: 54,
            backgroundColor: "currentColor",
            mask: `url(${RHAPSODY_LOGO}) no-repeat left center / contain`,
            WebkitMask: `url(${RHAPSODY_LOGO}) no-repeat left center / contain`,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: "#8b929c", mt: 0.25 }}>
        You&apos;re receiving this because you play with {companyName}. To stop
        receiving their calls, contact them directly.
      </Typography>
      <Typography
        component="a"
        onClick={show}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          mt: 1,
          fontSize: 12.5,
          color: SOFT,
          textDecoration: "underline",
          cursor: "pointer",
          "&:hover": { color: INK },
        }}
      >
        <i className="fa-regular fa-comment" style={{ fontSize: 12 }} />
        Chat with us
      </Typography>
    </Box>
  );
}

/** "#4A90E2" → "rgba(74, 144, 226, alpha)"; undefined when unparsable. */
function hexToRgba(hex: string, alpha: number): string | undefined {
  const value = hex.trim().replace(/^#/, "");
  if (value.length !== 3 && value.length !== 6) return undefined;
  const size = value.length === 3 ? 1 : 2;
  const channels = [0, 1, 2].map((index) =>
    parseInt(
      size === 1
        ? value[index] + value[index]
        : value.slice(index * 2, index * 2 + 2),
      16,
    ),
  );
  if (channels.some((channel) => Number.isNaN(channel))) return undefined;
  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
}

/**
 * Briefly tints a jumped-to block with the org's accent so the eye lands
 * where the ToC pointed: quick fade in, a beat, slow fade out. Imperative on
 * purpose — a transient effect on an element React renders for other
 * reasons; the stamp keeps overlapping flashes from wiping each other.
 */
function flashSection(element: HTMLElement, accent: string) {
  const tint = hexToRgba(accent, 0.16);
  if (!tint) return;

  const stamp = String(Date.now());
  element.dataset.callFlash = stamp;
  element.style.transition =
    "background-color 240ms ease-in, box-shadow 240ms ease-in";
  element.style.backgroundColor = tint;
  // The halo extends the tint past the text's edges without reflowing.
  element.style.boxShadow = `0 0 0 10px ${tint}`;
  element.style.borderRadius = "6px";

  window.setTimeout(() => {
    if (element.dataset.callFlash !== stamp) return;
    element.style.transition =
      "background-color 900ms ease-out, box-shadow 900ms ease-out";
    element.style.backgroundColor = "transparent";
    element.style.boxShadow = "0 0 0 10px rgba(0, 0, 0, 0)";
    window.setTimeout(() => {
      if (element.dataset.callFlash !== stamp) return;
      delete element.dataset.callFlash;
      element.style.removeProperty("transition");
      element.style.removeProperty("background-color");
      element.style.removeProperty("box-shadow");
      element.style.removeProperty("border-radius");
    }, 950);
  }, 1200);
}

/**
 * GitBook-style "On this page" rail: the visible blocks' titles as anchor
 * links, the one currently in view highlighted in the org's accent. Pinned
 * beside the centered column; the caller mounts it only on the standalone
 * page (never in embeds, where position:fixed would escape the drawer), and
 * it hides itself below the lg breakpoint where there is no room for a rail.
 *
 * @param root0 props
 * @param root0.mission the public mission map
 * @param root0.theme resolved call theme (modern, accent already corrected)
 * @returns {ReactElement} the rail, or null with fewer than two entries
 */
/** Shared engine of the "On this page" navigation — the desktop rail and the
 * mobile bar render the same entries, active tracking and jump behavior. */
function useCallPageToc(mission: MissionMap, theme: CallTheme) {
  const entries = visibleCallSections(mission);
  const [active, setActive] = useState("");
  // A clicked entry IS the selection until the user scrolls again: while the
  // jump travels (and once it settles — possibly clamped at the page bottom,
  // where position truth would say something else), picking stays off.
  const jumpRef = useRef<{ target: number } | null>(null);
  const anchorsKey = entries.map((entry) => entry.anchor).join(",");

  useEffect(() => {
    const anchored = anchorsKey
      .split(",")
      .map((anchor) => document.getElementById(anchor))
      .filter((element): element is HTMLElement => Boolean(element));
    if (!anchored.length) return undefined;
    // The active entry is the last block whose top passed the reading line —
    // a quarter down the viewport. Pinned to the bottom, the tail blocks may
    // never reach that line, so the last one lights up instead. A handful of
    // rect reads per scroll event is nothing at this element count.
    const pick = () => {
      const jump = jumpRef.current;
      if (jump) {
        // Scroll events from our own jump: hold the clicked selection. The
        // settle event clears the hold, so the NEXT event is the user's.
        if (Math.abs(window.scrollY - jump.target) <= 4) jumpRef.current = null;
        return;
      }
      const line = window.innerHeight * 0.25;
      let current = anchored[0];
      for (const element of anchored) {
        if (element.getBoundingClientRect().top <= line) current = element;
      }
      const scroller = document.scrollingElement;
      if (
        scroller &&
        window.scrollY + window.innerHeight >= scroller.scrollHeight - 2
      ) {
        current = anchored[anchored.length - 1];
      }
      setActive(current.id);
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true });
    window.addEventListener("resize", pick);
    return () => {
      window.removeEventListener("scroll", pick);
      window.removeEventListener("resize", pick);
    };
  }, [anchorsKey]);

  const jump = (anchor: string) => {
    const element = document.getElementById(anchor);
    if (!element) return;
    // Not scrollIntoView: Chrome cancels an element-tracked smooth scroll
    // when the rail re-renders its active entry mid-flight. An absolute
    // window target survives it. The offset mirrors the sections'
    // responsive scroll-margin-top (the mobile bar is sticky and taller);
    // the clamp makes the settle position detectable even when the block
    // is too low to reach the top.
    const offset = window.matchMedia("(min-width: 1200px)").matches ? 16 : 56;
    const max =
      (document.scrollingElement?.scrollHeight ?? 0) - window.innerHeight;
    const target = Math.min(
      Math.max(0, max),
      Math.max(0, window.scrollY + element.getBoundingClientRect().top - offset),
    );
    const held = { target };
    jumpRef.current = held;
    // An interrupted jump (user wheels mid-flight) never settles on the
    // target — release the hold shortly after, unless a newer click owns
    // it by then.
    window.setTimeout(() => {
      if (jumpRef.current === held) jumpRef.current = null;
    }, 1500);
    setActive(anchor);
    flashSection(element, theme.accent);
    window.scrollTo({ top: target, behavior: "smooth" });
  };

  return { entries, active, jump };
}

/** The entry links, shared by the desktop rail and the mobile bar. */
function TocEntries({
  entries,
  active,
  theme,
  onJump,
}: {
  entries: { anchor: string; label: string }[];
  active: string;
  theme: CallTheme;
  onJump: (anchor: string) => void;
}) {
  return (
    <Box
      sx={{ borderLeft: HAIRLINE, display: "flex", flexDirection: "column" }}
    >
      {entries.map((entry) => {
        const isActive = active === entry.anchor;
        return (
          <Typography
            key={entry.anchor}
            component="a"
            href={`#${entry.anchor}`}
            onClick={(event) => {
              event.preventDefault();
              onJump(entry.anchor);
            }}
            sx={{
              fontSize: 13,
              lineHeight: 1.4,
              py: 0.5,
              pl: 1.5,
              // The active bar replaces the rail hairline instead of
              // sitting next to it.
              ml: "-1px",
              textDecoration: "none",
              color: isActive ? theme.accentText : MUTED,
              fontWeight: isActive ? 600 : 400,
              borderLeft: isActive
                ? `2px solid ${theme.accentText}`
                : "2px solid transparent",
              "&:hover": {
                color: isActive ? theme.accentText : INK,
                // The app's global anchor style underlines on hover;
                // the rail signals with color alone, like GitBook.
                textDecoration: "none",
              },
            }}
          >
            {entry.label}
          </Typography>
        );
      })}
    </Box>
  );
}

export function CallPageToc({
  mission,
  theme,
}: {
  mission: MissionMap;
  theme: CallTheme;
}): ReactElement | null {
  const { entries, active, jump } = useCallPageToc(mission, theme);

  if (entries.length < 2) return null;

  return (
    <Box
      component="nav"
      aria-label="On this page"
      sx={{
        position: "fixed",
        top: 96,
        // Right of the 680px column (half = 340) plus a 20px gutter.
        left: "calc(50% + 360px)",
        width: 200,
        display: { xs: "none", lg: "block" },
        maxHeight: "calc(100vh - 128px)",
        overflowY: "auto",
        fontFamily: theme.fontStack,
        "& .MuiTypography-root": { fontFamily: "inherit" },
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: MUTED,
          pb: 0.75,
        }}
      >
        On this page
      </Typography>
      <TocEntries
        entries={entries}
        active={active}
        theme={theme}
        onJump={jump}
      />
    </Box>
  );
}

/**
 * The mobile "On this page": a GitBook-style collapsible bar pinned to the
 * top of the viewport, shown exactly where the rail is not (below lg). It
 * lives IN the column flow (sticky, not fixed), so embeds would stay safe —
 * though the caller gates it to the standalone page like the rail.
 *
 * @param root0 props
 * @param root0.mission the public mission map
 * @param root0.theme resolved call theme (modern, accent already corrected)
 * @returns {ReactElement} the bar, or null with fewer than two entries
 */
export function CallPageTocBar({
  mission,
  theme,
}: {
  mission: MissionMap;
  theme: CallTheme;
}): ReactElement | null {
  const { entries, active, jump } = useCallPageToc(mission, theme);
  const [open, setOpen] = useState(false);

  // Scrolling away is a dismissal — the pinned bar shouldn't drag an open
  // menu over the content. (Menu taps close it BEFORE the jump scrolls.)
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    return () => window.removeEventListener("scroll", close);
  }, [open]);

  if (entries.length < 2) return null;

  return (
    <Box
      component="nav"
      aria-label="On this page"
      sx={{
        display: { xs: "block", lg: "none" },
        position: "sticky",
        top: 0,
        zIndex: 10,
        // Bleed over the column padding so the pinned bar reads full-width.
        alignSelf: "stretch",
        mx: -2,
        px: 2,
        background: "#ffffff",
        borderBottom: HAIRLINE,
        fontFamily: theme.fontStack,
        "& .MuiTypography-root": { fontFamily: "inherit" },
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          py: 1.25,
          background: "none",
          border: 0,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <i
          className="fa-solid fa-list-ul"
          style={{ fontSize: 11, color: MUTED }}
        />
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: MUTED,
          }}
        >
          On this page
        </Typography>
        <i
          className={`fa-solid fa-chevron-${open ? "up" : "down"}`}
          style={{ fontSize: 10, color: MUTED, marginLeft: "auto" }}
        />
      </Box>
      {open ? (
        // An OVERLAY, not a flow child: expanding must not push the page
        // down — and collapsing on tap must not shift the layout between
        // measuring the jump target and scrolling to it (which buried the
        // target under the bar by exactly the panel's height).
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#ffffff",
            borderBottom: HAIRLINE,
            boxShadow: "0 12px 24px rgba(15, 18, 22, 0.08)",
            px: 2,
            pb: 1.25,
            maxHeight: "min(60vh, 420px)",
            overflowY: "auto",
          }}
        >
          <TocEntries
            entries={entries}
            active={active}
            theme={theme}
            onJump={(anchor) => {
              setOpen(false);
              jump(anchor);
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
